/**
 * USP banner — desktop marquee when the icon/large row overflows.
 *
 * Uses a plain init (not a custom element) so Shopify theme hot-reload always
 * picks up new logic. Mobile rotating marquee is CSS-driven; stacked never tickers.
 *
 * Wrapped in an IIFE so the theme editor can re-inject the deferred asset without
 * hitting top-level const redeclaration errors. State lives on window so a new run
 * can tear down (and avoid double-enhancing) instances left by the previous one.
 */
(() => {
  const state = (window.__meacoUspBanner ??= { instances: new Map(), listeners: null });

  /** Component -> instance. Iterable so orphaned instances can be swept. */
  const instances = state.instances;

  /** Identifies instances created by this execution of the script. */
  const RUN = Symbol('usp-banner-run');

  /**
   * @param {CSSStyleDeclaration} styles
   * @param {Element} root
   */
  const readGap = (styles, root) => {
    const raw = styles.columnGap && styles.columnGap !== 'normal' ? styles.columnGap : styles.gap;
    let gap = Number.parseFloat(raw);

    if (!Number.isFinite(gap) || gap < 0) {
      const rootStyles = window.getComputedStyle(root);
      gap =
        Number.parseFloat(rootStyles.getPropertyValue('--usp-gap-effective')) ||
        Number.parseFloat(rootStyles.getPropertyValue('--usp-gap')) ||
        0;
    }

    return gap;
  };

  /**
   * Intrinsic width of the primary content row (clone ignored).
   * @param {HTMLElement} content
   * @param {Element} root
   */
  const measureContentWidth = (content, root) => {
    const styles = window.getComputedStyle(content);
    const gap = readGap(styles, root);
    const children = Array.from(content.children).filter((child) => child instanceof HTMLElement);

    let childrenWidth = 0;
    for (const [index, child] of children.entries()) {
      if (index > 0) childrenWidth += gap;
      childrenWidth += child.getBoundingClientRect().width;
    }

    return Math.ceil(
      Math.max(childrenWidth, content.scrollWidth, content.getBoundingClientRect().width)
    );
  };

  /**
   * A scroll container is only usable by keyboard once it is focusable, and it
   * needs a name to be announced. Attributes are removed again as soon as the row
   * fits, so a banner that never overflows stays out of the tab order.
   * @param {HTMLElement} viewport
   * @param {boolean} scrollable
   */
  const setScrollable = (viewport, scrollable) => {
    if (scrollable) {
      viewport.setAttribute('tabindex', '0');
      viewport.setAttribute('role', 'group');

      const label = viewport.dataset.scrollLabel;
      if (label) viewport.setAttribute('aria-label', label);

      return;
    }

    viewport.removeAttribute('tabindex');
    viewport.removeAttribute('role');
    viewport.removeAttribute('aria-label');
    viewport.scrollLeft = 0;
  };

  /**
   * Overflow ticker / manual scroll should clip at the screen edges, not the
   * content-column margins (Figma mobile + desktop overflow).
   * @param {Element} root
   * @param {boolean} enabled
   */
  const setEdgeBleed = (root, enabled) => {
    root.classList.toggle('usp-banner--edge-bleed', enabled);
  };

  /**
   * Clones re-render the same blocks for seamless looping. Strip editor-only
   * attributes so Shopify does not see duplicate block targets in the customizer.
   * @param {HTMLElement} component
   */
  const stripCloneEditorAttributes = (component) => {
    const clone = component.querySelector('[data-usp-clone]');
    if (!clone) return;

    for (const el of clone.querySelectorAll('[data-shopify-editor-block]')) {
      el.removeAttribute('data-shopify-editor-block');
    }

    for (const el of clone.querySelectorAll('[id^="shopify-block-"]')) {
      el.removeAttribute('id');
    }
  };

  /**
   * @param {HTMLElement} component
   */
  const sync = (component) => {
    const root = component.closest('.usp-banner');
    const viewport = component.querySelector('[data-usp-viewport]');
    const content = component.querySelector('[data-usp-content]');

    if (!root || !viewport || !content) return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const layoutStyle = component.dataset.layoutStyle || 'icon';
    const mobileLayout = component.dataset.mobileLayout || 'rotating';
    const isStackedMobile = isMobile && layoutStyle === 'large' && mobileLayout === 'stacked';
    const isMobileRotating = isMobile && !isStackedMobile;
    let marquee = false;

    if (isStackedMobile) {
      setScrollable(viewport, false);
      setEdgeBleed(root, false);
      root.classList.toggle('usp-banner--marquee', false);
      return;
    }

    /* Force a static layout paint before measuring */
    void viewport.offsetWidth;

    const contentWidth = measureContentWidth(content, root);
    /*
      Compare against the component (content-column) width, not the viewport.
      When edge-bleed is active the viewport is 100vw; measuring it would clear
      overflow and flicker the bleed class on every sync.
    */
    const columnWidth = Math.floor(component.clientWidth);
    const overflows = contentWidth > columnWidth + 1;

    /*
      Reduced motion keeps the static centered row. The clone and max-content track
      only read as a ticker while the animation runs; left in place they would show
      duplicated, clipped content instead. An overflowing row scrolls manually
      (see the reduced-motion styles) so no selling point is stranded off-screen.
    */
    if (prefersReducedMotion) {
      setScrollable(viewport, overflows);
      setEdgeBleed(root, overflows);
      root.classList.toggle('usp-banner--marquee', false);
      return;
    }

    setScrollable(viewport, false);

    /* Mobile rotating ticker always bleeds edge-to-edge */
    if (isMobileRotating) {
      setEdgeBleed(root, true);
      root.classList.toggle('usp-banner--marquee', false);
      return;
    }

    if (overflows) {
      marquee = true;
      setEdgeBleed(root, true);
    } else {
      setEdgeBleed(root, false);
    }

    /*
      Toggle is a no-op when the class is already present, so resize / font-load
      syncs do not restart the CSS animation from the beginning.
    */
    root.classList.toggle('usp-banner--marquee', marquee);
  };

  /**
   * @param {HTMLElement} component
   */
  const enhance = (component) => {
    const existing = instances.get(component);
    if (existing) {
      if (existing.run === RUN) {
        existing.sync();
        return;
      }

      /* Stale logic from an earlier execution of the script — rebuild it */
      existing.teardown();
    }

    const root = component.closest('.usp-banner');
    const viewport = component.querySelector('[data-usp-viewport]');
    const content = component.querySelector('[data-usp-content]');

    if (!root || !viewport || !content) return;

    stripCloneEditorAttributes(component);

    const runSync = () => sync(component);
    let resizeTimer = 0;

    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(runSync, 80);
    };

    const mediaMobile = window.matchMedia('(max-width: 767px)');
    const mediaMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(viewport);
    resizeObserver.observe(content);

    mediaMobile.addEventListener('change', runSync);
    mediaMotion.addEventListener('change', runSync);
    window.addEventListener('resize', onResize);

    const teardown = () => {
      resizeObserver.disconnect();
      mediaMobile.removeEventListener('change', runSync);
      mediaMotion.removeEventListener('change', runSync);
      window.removeEventListener('resize', onResize);
      window.clearTimeout(resizeTimer);
      instances.delete(component);
    };

    instances.set(component, { run: RUN, sync: runSync, teardown });

    runSync();

    if (document.fonts?.ready) {
      document.fonts.ready.then(runSync).catch(() => {});
    }
  };

  /**
   * @param {Element} container
   */
  const teardownWithin = (container) => {
    for (const [component, instance] of instances) {
      if (container.contains(component)) instance.teardown();
    }
  };

  /* Safety net for markup swapped out without a section lifecycle event */
  const teardownDetached = () => {
    for (const [component, instance] of instances) {
      if (!component.isConnected) instance.teardown();
    }
  };

  const initAll = () => {
    teardownDetached();

    document.querySelectorAll('[data-usp-banner]').forEach((node) => {
      if (node instanceof HTMLElement) enhance(node);
    });
  };

  /**
   * @param {Event} event
   */
  const onSectionUnload = (event) => {
    /* The section subtree is still traversable here, even once detached */
    if (event.target instanceof Element) teardownWithin(event.target);

    teardownDetached();
  };

  initAll();

  const listeners = [
    ['shopify:section:load', initAll],
    ['shopify:section:reorder', initAll],
    ['shopify:section:select', initAll],
    ['shopify:section:unload', onSectionUnload],
  ];

  /* Drop the previous execution's handlers so document listeners never stack up */
  for (const [type, handler] of state.listeners ?? []) {
    document.removeEventListener(type, handler);
  }

  for (const [type, handler] of listeners) {
    document.addEventListener(type, handler);
  }

  state.listeners = listeners;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  }
})();
